import {
	AssetPropertyCapabilityFilterParam,
	AssetSchema,
	AssetSchemaRaw,
	GeometryType,
} from '../types/asset.types';
import {
	Capabilities,
	Capability,
	InspectionsStandards,
	InspectionTypes,
	RiskCategory,
} from '../types/capabilities/capabilities.asset.types';
import {
	isSingleProperty,
	NestedProperty,
	Property,
	SingleProperty,
} from '../types/property.types';

import { SystemType } from '../types/system.types';
import { newProperty } from './property.class';
import { FieldId, UnitFamily, getUnitFamilyOrFail } from './unit';

export type AssetSchemaParams = Omit<AssetSchemaRaw, 'SystemType'> & {
	SystemType: SystemType;
};

/**
 * Implementation of the AssetSchema interface.
 */
export class AssetSchemaImpl implements AssetSchema {
	systemType: SystemType;
	id: string;
	displayName: string;
	pluralName: string;
	defaultColour: string;
	defaultIcon?: string;
	defaultMinZoom: number;
	geometryType: GeometryType;
	categories: string[];
	private readonly properties: Property[];
	private readonly capabilities: Capability[];
	private readonly inspectionStandards: InspectionsStandards[];
	private readonly riskCategories: RiskCategory[];

	/**
	 * Constructs an AssetSchemaImpl instance.
	 * @internal
	 * @param params - The parameters for the asset schema.
	 */
	constructor(params: AssetSchemaParams) {
		this.systemType = params.SystemType;
		this.id = params.AssetType;
		this.displayName = params.DisplayName;
		this.pluralName = params.PluralName;
		this.geometryType = params.GeometryType;
		this.properties = [];
		for (const rawProperty of params.Properties ?? []) {
			this.properties.push(newProperty(rawProperty));
		}
		this.defaultColour = params.DefaultColour ?? '#000000';
		this.defaultIcon = params.DefaultIcon;
		this.defaultMinZoom = params.DefaultMinZoom ?? 16;
		this.categories = params.Categories ?? [];
		this.capabilities = [];
		this.inspectionStandards = params.InspectionStandards ?? [];
		this.riskCategories = params.RiskCategories ?? [];

		for (const capability of Capabilities) {
			if (params[capability]) {
				this.capabilities.push(capability);
			}
		}
	}

	/**
	 * Gets the full display name of the asset.
	 * @returns The full display name.
	 */
	get fullDisplayName(): string {
		const assetDisplayName = this.displayName;
		const systemDisplayName = this.systemType.displayName;
		return `${systemDisplayName} - ${assetDisplayName}`;
	}

	/**
	 * Checks if the asset has a specific capability.
	 * @param capability - The capability to check.
	 * @returns True if the asset has the capability, false otherwise.
	 */
	hasCapability(capability: Capability): boolean {
		// THIS IS ENABLING INSPECTIONS RECORD TYPE IN REHAB
		if (capability === 'Inspections') {
			return this.inspectionStandards.length > 0;
		}
		return this.capabilities.includes(capability);
	}

	/**
	 * Gets the inspection types of the asset.
	 * @returns An array of inspection types.
	 * @deprecated Use getInspectionStandards() instead.
	 */
	getInspectionTypes(): InspectionTypes[] {
		return this.getInspectionStandards();
	}

	/**
	 * Gets the inspection types of the asset.
	 * @returns An array of inspection types.
	 */
	getInspectionStandards(): InspectionsStandards[] {
		return this.inspectionStandards;
	}

	/**
	 * Checks if the asset has a specific inspection type.
	 * @param inspectionStandard - The inspection type to check.
	 * @returns True if the asset has the inspection type, false otherwise.
	 */
	hasInspectionType(
		inspectionStandard: InspectionTypes | InspectionsStandards,
	): boolean {
		return this.inspectionStandards.includes(inspectionStandard);
	}

	/**
	 * Gets the flat properties of the asset, including nested properties.
	 * @param filter - Optional filter to apply to the properties.
	 * @returns An array of single properties.
	 */
	getFlatProperties(
		filter?: AssetPropertyCapabilityFilterParam,
	): SingleProperty[] {
		return this.getProperties(filter)
			.flatMap(p => (p as NestedProperty).properties ?? p)
			.filter(p => isSingleProperty(p));
	}

	/**
	 * Gets the properties of the asset.
	 * @param filter - Optional filter to apply to the properties.
	 * @returns An array of properties.
	 */
	getProperties(filter?: AssetPropertyCapabilityFilterParam): Property[] {
		if (!filter) {
			return this.properties;
		}
		const checkFilter = (property: SingleProperty): boolean => {
			if ('capability' in filter) {
				return property.hasCapability(filter.capability);
			}

			if ('anyCapability' in filter) {
				return filter.anyCapability.some(capability =>
					property.hasCapability(capability),
				);
			}

			if ('showOnMapPanel' in filter) {
				return (
					property.hasCapability('ShowOnMapPanel') ===
					filter.showOnMapPanel
				);
			}

			return false;
		};
		const properties = [];
		for (const property of this.properties) {
			if (property.type !== 'List') {
				if (checkFilter(property)) {
					properties.push(property);
				}
			} else {
				const nestedProperties = [];
				for (const nestedProperty of property.properties) {
					if (checkFilter(nestedProperty)) {
						nestedProperties.push(nestedProperty);
					}
				}
				if (nestedProperties.length > 0) {
					properties.push(
						property.copyWithNewProperties(nestedProperties),
					);
				}
			}
		}
		return properties;
	}

	/**
	 * Gets a flat property by its ID, including nested properties.
	 * @param id - The ID of the property.
	 * @returns The property if found, undefined otherwise.
	 */
	getFlatProperty(id: string): Property | undefined {
		for (const property of this.properties) {
			if (property.id === id) {
				return property;
			}
			if (property.type === 'List') {
				for (const nestedProperty of property.properties) {
					if (nestedProperty.id === id) {
						return nestedProperty;
					}
				}
			}
		}
		return undefined;
	}

	/**
	 * Gets a property by any key.
	 * @param key - The key to search by.
	 * @param value - The value to search for.
	 * @returns The property if found, undefined otherwise.
	 */
	getPropertyByAnyKey<T extends keyof Property>(
		key: T,
		value: Property[T],
	): Property | undefined {
		for (const property of this.properties) {
			if (property[key] === value) {
				return property;
			}
			if (property.type === 'List') {
				for (const nestedProperty of property.properties) {
					if (nestedProperty[key] === value) {
						return nestedProperty;
					}
				}
			}
		}
		return undefined;
	}

	/**
	 * Gets the risk categories of the asset.
	 * @param filter - Optional filter to apply to the risk categories.
	 * @returns An array of risk categories.
	 */
	getRiskCategories(filter?: 'Lof' | 'Cof'): RiskCategory[] {
		if (!filter) {
			return this.riskCategories;
		}
		if (filter === 'Lof') {
			return this.riskCategories.filter(
				category => category.Lof === true,
			);
		}
		return this.riskCategories.filter(category => category.Cof === true);
	}

	getUnitFamilyMap(): Record<FieldId, UnitFamily> {
		return this.getFlatProperties()
			.filter(property => property.units)
			.reduce<Record<FieldId, UnitFamily>>((unitFamilyMap, property) => {
				const unitFamily = getUnitFamilyOrFail(property.units!);

				if (unitFamily) {
					unitFamilyMap[property.id] = unitFamily;
				}
				return unitFamilyMap;
			}, {});
	}
}
